import React, { useCallback, useEffect, useRef, useState } from 'react';
import { OrderFilterParams } from 'hooks/useOrderFiltersNew';
import styles from './FiltersTablet.module.scss';
import iconFilter from './filtersTablet/iconFilter.svg';
import { ReactComponent as IconCloseBlue } from './img/iconCloseBlue.svg';
import { ReactComponent as IconChevron } from '../../assets/img/filter/iconChevronDown.svg';
import { ReactComponent as IconChevronUp } from '../../assets/img/filter/iconChevronUp.svg';
import { ReactComponent as IconWork } from './filtersTablet/iconWork.svg';
import { ReactComponent as IconOrdersStatus } from './filtersTablet/iconOrdersStatus.svg';
import { ReactComponent as IconCalculator } from './filtersTablet/iconCalculator.svg';
import { ReactComponent as IconCalendar } from './filtersTablet/iconCalendar.svg';
import { ReactComponent as IconPerson } from './filtersTablet/iconPerson.svg';
import { ReactComponent as IconPayment } from './filtersTablet/iconPayment.svg';
import { ReactComponent as IconBookOpen } from './filtersTablet/iconBookOpen.svg';
import { ReactComponent as IconBill } from './filtersTablet/iconBill.svg';
import { ReactComponent as IconDone } from './img/iconDone.svg';
import { ReactComponent as IconSearchNumber } from './filtersTablet/iconSearchNumber.svg';
import { ReactComponent as IconClipboardCheck } from './filtersTablet/iconClipboardCheck.svg';
import {
  useGetOrderFiltersQuery,
  useGetOrderTemplatesFiltersQuery,
  useGetTypesFiltersQuery,
} from 'redux/Api/ordersApi/ordersApi';
import { ActFilterData } from 'components/OrdersFilters/ActFilter';
import { ReceiptFilterData } from 'components/OrdersFilters/ReceiptFilter';
import { AdaptiveFilterOption } from 'components/OrdersFilters/AdaptiveFilter';
import { TabletAdaptiveFilter } from 'components/OrdersFiltersTablet/TabletAdaptiveFilter';
import { TabletPriceFilter } from 'components/OrdersFiltersTablet/TabletPriceFilter';
import { TabletDateFilter } from 'components/OrdersFiltersTablet/TabletDateFilter';
import { TabletReceiptFilter } from 'components/OrdersFiltersTablet/TabletReceiptFilter';
import { TabletActFilter } from 'components/OrdersFiltersTablet/TabletActFilter';
import { Button } from 'ui/Button';
import { useGetEmployedsQuery } from 'redux/Api/employedsApi/employedsApi';
import { SelectedItem } from 'components/EmployedsFilters/EmployedsAdaptiveFilter';
import { useGetTabPaginationEmployedsQuery } from 'redux/Api/employedsWorkSupportApi/employedsWorkSupportApi';
import { useGetTabPaginationBeneficiarQuery } from 'redux/Api/beneficiariesApi/beneficiariesApi';
import { ItemTypes } from 'redux/Api/beneficiariesApi/type';

interface FiltersTabletProps {
  onApplyFilters: (filters: OrderFilterParams) => void;
  onResetFilters: () => void;
  initialFilters?: OrderFilterParams;
  resetTrigger?: number;
}

const defaultPopoverStates = {
  customers: false,
  types: false,
  statuses: false,
  amount: false,
  numberOrder: false,
  date: false,
  workers: false,
  presets: false,
  receipts: false,
  acts: false,
};

type SectionKey =
  | 'serviceType'
  | 'paymentType'
  | 'customers'
  | 'status'
  | 'amount'
  | 'numberOrder'
  | 'date'
  | 'executor'
  | 'template'
  | 'check'
  | 'act';

export const FiltersTablet: React.FC<FiltersTabletProps> = ({
  onApplyFilters,
  onResetFilters,
  initialFilters = {},
  resetTrigger = 0,
}) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [openSection, setOpenSection] = useState<SectionKey | null>(null);
  const filtersRef = useRef<HTMLDivElement>(null);
  const role = localStorage.getItem('role');

  const toggleSection = (key: SectionKey) => {
    setOpenSection((prev) => (prev === key ? null : key));
  };

  const handleReset = () => {
    onResetFilters();
    setIsFiltersOpen(false);
    setOpenSection(null);
  };

  const { data: filtersData } = useGetOrderFiltersQuery();

  const [typeIds, setTypeIds] = useState<SelectedItem[]>([]);
  const [customerIds, setCustomerIds] = useState<SelectedItem[]>(
    initialFilters.customer_ids || [],
  );
  const [workerIds, setWorkerIds] = useState<SelectedItem[]>([]);
  const [presetIds, setPresetIds] = useState<SelectedItem[]>([]);
  const [statusIds, setStatusIds] = useState<SelectedItem[]>([]);
  const [paymentTypeIds, setPaymentTypeIds] = useState<SelectedItem[]>(
    initialFilters.payment_type_ids || [],
  );
  const [amountFrom, setAmountFrom] = useState<number | undefined>(
    initialFilters.amount_from,
  );
  const [amountTo, setAmountTo] = useState<number | undefined>(initialFilters.amount_to);
  const [numbersRangeStart, setNumbersRangeStart] = useState<number | undefined>(
    initialFilters.numbers_range?.start !== undefined &&
      initialFilters.numbers_range.start !== ''
      ? parseInt(initialFilters.numbers_range.start, 10)
      : undefined,
  );
  const [numbersRangeEnd, setNumbersRangeEnd] = useState<number | undefined>(
    initialFilters.numbers_range?.end !== undefined &&
      initialFilters.numbers_range.end !== ''
      ? parseInt(initialFilters.numbers_range.end, 10)
      : undefined,
  );
  const [startDateStart, setStartDateStart] = useState<Date | undefined>(
    initialFilters.date_start_range?.start
      ? new Date(initialFilters.date_start_range.start)
      : undefined,
  );
  const [startDateEnd, setStartDateEnd] = useState<Date | undefined>(
    initialFilters.date_start_range?.end &&
      initialFilters.date_start_range.end !== initialFilters.date_start_range.start
      ? new Date(initialFilters.date_start_range.end)
      : initialFilters.date_start_range?.start
      ? new Date(initialFilters.date_start_range.start)
      : undefined,
  );
  const [endDateStart, setEndDateStart] = useState<Date | undefined>(
    initialFilters.date_end_range?.start
      ? new Date(initialFilters.date_end_range.start)
      : undefined,
  );
  const [endDateEnd, setEndDateEnd] = useState<Date | undefined>(
    initialFilters.date_end_range?.end &&
      initialFilters.date_end_range.end !== initialFilters.date_end_range.start
      ? new Date(initialFilters.date_end_range.end)
      : initialFilters.date_end_range?.start
      ? new Date(initialFilters.date_end_range.start)
      : undefined,
  );
  const [creationDateStart, setCreationDateStart] = useState<Date | undefined>(
    initialFilters.create_date_range?.start
      ? new Date(initialFilters.create_date_range.start)
      : undefined,
  );
  const [creationDateEnd, setCreationDateEnd] = useState<Date | undefined>(
    initialFilters.create_date_range?.end &&
      initialFilters.create_date_range.end !== initialFilters.create_date_range.start
      ? new Date(initialFilters.create_date_range.end)
      : initialFilters.create_date_range?.start
      ? new Date(initialFilters.create_date_range.start)
      : undefined,
  );
  const [paidDateStart, setPaidDateStart] = useState<Date | undefined>(
    initialFilters.date_paid_range?.start
      ? new Date(initialFilters.date_paid_range.start)
      : undefined,
  );
  const [paidDateEnd, setPaidDateEnd] = useState<Date | undefined>(
    initialFilters.date_paid_range?.end &&
      initialFilters.date_paid_range.end !== initialFilters.date_paid_range.start
      ? new Date(initialFilters.date_paid_range.end)
      : initialFilters.date_paid_range?.start
      ? new Date(initialFilters.date_paid_range.start)
      : undefined,
  );
  const [actData, setActData] = useState<ActFilterData>({
    number: initialFilters.act?.number,
    statuses: initialFilters.act?.statuses || [],
    create_date_range: initialFilters.act?.create_date_range,
  });
  const [receiptData, setReceiptData] = useState<ReceiptFilterData>({
    number: initialFilters.receipts?.number,
    statuses: initialFilters.receipts?.statuses || [],
  });

  const [popoverStates, setPopoverStates] = useState(defaultPopoverStates);

  const closePopover = (popoverKey: keyof typeof popoverStates) => {
    setPopoverStates((prev) => ({ ...prev, [popoverKey]: false }));
  };

  const closeAllPopovers = () => {
    setPopoverStates({
      customers: false,
      types: false,
      statuses: false,
      amount: false,
      date: false,
      numberOrder: false,
      workers: false,
      presets: false,
      receipts: false,
      acts: false,
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filtersRef.current && !filtersRef.current.contains(event.target as Node)) {
        setIsFiltersOpen(false);
        setOpenSection(null);
      }
    };

    if (isFiltersOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFiltersOpen]);

  useEffect(() => {
    setTypeIds(initialFilters.type_ids || []);
    setCustomerIds(initialFilters.customer_ids || []);
    setWorkerIds(initialFilters.worker_ids || []);
    setPresetIds(initialFilters.preset_ids || []);
    setStatusIds(initialFilters.statuses || []);
    setPaymentTypeIds(initialFilters.payment_type_ids || []);
    setAmountFrom(initialFilters.amount_from);
    setAmountTo(initialFilters.amount_to);
    setNumbersRangeStart(
      initialFilters.numbers_range?.start !== undefined &&
        initialFilters.numbers_range.start !== ''
        ? parseInt(initialFilters.numbers_range.start, 10)
        : undefined,
    );
    setNumbersRangeEnd(
      initialFilters.numbers_range?.end !== undefined &&
        initialFilters.numbers_range.end !== ''
        ? parseInt(initialFilters.numbers_range.end, 10)
        : undefined,
    );

    setStartDateStart(
      initialFilters.date_start_range?.start
        ? new Date(initialFilters.date_start_range.start)
        : undefined,
    );
    setStartDateEnd(
      initialFilters.date_start_range?.end &&
        initialFilters.date_start_range.end !== initialFilters.date_start_range.start
        ? new Date(initialFilters.date_start_range.end)
        : initialFilters.date_start_range?.start
        ? new Date(initialFilters.date_start_range.start)
        : undefined,
    );
    setEndDateStart(
      initialFilters.date_end_range?.start
        ? new Date(initialFilters.date_end_range.start)
        : undefined,
    );
    setEndDateEnd(
      initialFilters.date_end_range?.end &&
        initialFilters.date_end_range.end !== initialFilters.date_end_range.start
        ? new Date(initialFilters.date_end_range.end)
        : initialFilters.date_end_range?.start
        ? new Date(initialFilters.date_end_range.start)
        : undefined,
    );
    setCreationDateStart(
      initialFilters.create_date_range?.start
        ? new Date(initialFilters.create_date_range.start)
        : undefined,
    );
    setCreationDateEnd(
      initialFilters.create_date_range?.end &&
        initialFilters.create_date_range.end !== initialFilters.create_date_range.start
        ? new Date(initialFilters.create_date_range.end)
        : initialFilters.create_date_range?.start
        ? new Date(initialFilters.create_date_range.start)
        : undefined,
    );
    setPaidDateStart(
      initialFilters.date_paid_range?.start
        ? new Date(initialFilters.date_paid_range.start)
        : undefined,
    );
    setPaidDateEnd(
      initialFilters.date_paid_range?.end &&
        initialFilters.date_paid_range.end !== initialFilters.date_paid_range.start
        ? new Date(initialFilters.date_paid_range.end)
        : initialFilters.date_paid_range?.start
        ? new Date(initialFilters.date_paid_range.start)
        : undefined,
    );

    setActData({
      number: initialFilters.act?.number,
      statuses: initialFilters.act?.statuses || [],
      create_date_range: initialFilters.act?.create_date_range,
    });
    setReceiptData({
      number: initialFilters.receipts?.number,
      statuses: initialFilters.receipts?.statuses || [],
    });
  }, [initialFilters]);

  useEffect(() => {
    if (resetTrigger > 0) {
      setTypeIds([]);
      setCustomerIds([]);
      setWorkerIds([]);
      setPresetIds([]);
      setStatusIds([]);
      setPaymentTypeIds([]);
      setAmountFrom(undefined);
      setAmountTo(undefined);
      setNumbersRangeStart(undefined);
      setNumbersRangeEnd(undefined);
      setStartDateStart(undefined);
      setStartDateEnd(undefined);
      setEndDateStart(undefined);
      setEndDateEnd(undefined);
      setCreationDateStart(undefined);
      setCreationDateEnd(undefined);
      setPaidDateStart(undefined);
      setPaidDateEnd(undefined);
      setActData({
        number: undefined,
        statuses: [],
        create_date_range: undefined,
      });
      setReceiptData({
        number: undefined,
        statuses: [],
      });
    }
  }, [resetTrigger]);

  const getCurrentFilters = (): OrderFilterParams => {
    const filters: OrderFilterParams = {};

    if (typeIds.length > 0) filters.type_ids = typeIds;
    if (customerIds.length > 0) filters.customer_ids = customerIds;
    if (workerIds.length > 0) filters.worker_ids = workerIds;
    if (presetIds.length > 0) filters.preset_ids = presetIds;
    if (statusIds.length > 0) filters.statuses = statusIds;
    if (paymentTypeIds.length > 0) filters.payment_type_ids = paymentTypeIds;
    if (amountFrom !== undefined) filters.amount_from = amountFrom;
    if (amountTo !== undefined) filters.amount_to = amountTo;

    if (numbersRangeStart !== undefined || numbersRangeEnd !== undefined) {
      filters.numbers_range = {
        start: numbersRangeStart !== undefined ? String(numbersRangeStart) : '',
        end: numbersRangeEnd !== undefined ? String(numbersRangeEnd) : '',
      };
    }

    if (startDateStart || startDateEnd) {
      filters.date_start_range = {
        start: startDateStart
          ? formatDateForAPI(startDateStart)
          : startDateEnd
          ? formatDateForAPI(startDateEnd)
          : '',
        end: startDateEnd
          ? formatDateForAPI(startDateEnd)
          : startDateStart
          ? formatDateForAPI(startDateStart)
          : '',
      };
    }

    if (endDateStart || endDateEnd) {
      filters.date_end_range = {
        start: endDateStart
          ? formatDateForAPI(endDateStart)
          : endDateEnd
          ? formatDateForAPI(endDateEnd)
          : '',
        end: endDateEnd
          ? formatDateForAPI(endDateEnd)
          : endDateStart
          ? formatDateForAPI(endDateStart)
          : '',
      };
    }

    if (creationDateStart || creationDateEnd) {
      filters.create_date_range = {
        start: creationDateStart
          ? formatDateForAPI(creationDateStart)
          : creationDateEnd
          ? formatDateForAPI(creationDateEnd)
          : '',
        end: creationDateEnd
          ? formatDateForAPI(creationDateEnd)
          : creationDateStart
          ? formatDateForAPI(creationDateStart)
          : '',
      };
    }

    if (paidDateStart || paidDateEnd) {
      filters.date_paid_range = {
        start: paidDateStart
          ? formatDateForAPI(paidDateStart)
          : paidDateEnd
          ? formatDateForAPI(paidDateEnd)
          : '',
        end: paidDateEnd
          ? formatDateForAPI(paidDateEnd)
          : paidDateStart
          ? formatDateForAPI(paidDateStart)
          : '',
      };
    }

    if (
      actData.number ||
      (actData.statuses && actData.statuses.length > 0) ||
      actData.create_date_range
    ) {
      filters.act = actData;
    }

    if (
      receiptData.number !== undefined ||
      (receiptData.statuses && receiptData.statuses.length > 0)
    ) {
      filters.receipts = receiptData;
    }

    return filters;
  };

  const handleApplyFilters = () => {
    const filterParams = getCurrentFilters();
    onApplyFilters(filterParams);
    closeAllPopovers();
    setIsFiltersOpen(false);
    setOpenSection(null);
  };

  const handleResetIndividualFilter = (filterType: string) => {
    const updatedFilters = getCurrentFilters();

    switch (filterType) {
      case 'types':
        setTypeIds([]);
        delete updatedFilters.type_ids;
        closePopover('types');
        break;
      case 'customers':
        setCustomerIds([]);
        delete updatedFilters.customer_ids;
        closePopover('customers');
        break;
      case 'workers':
        setWorkerIds([]);
        delete updatedFilters.worker_ids;
        closePopover('workers');
        break;
      case 'presets':
        setPresetIds([]);
        delete updatedFilters.preset_ids;
        closePopover('presets');
        break;
      case 'statuses':
        setStatusIds([]);
        delete updatedFilters.statuses;
        closePopover('statuses');
        break;
      case 'paymentType':
        setPaymentTypeIds([]);
        delete updatedFilters.payment_type_ids;
        break;
      case 'amount':
        setAmountFrom(undefined);
        setAmountTo(undefined);
        delete updatedFilters.amount_from;
        delete updatedFilters.amount_to;
        closePopover('amount');
        break;
      case 'numberOrder':
        setNumbersRangeStart(undefined);
        setNumbersRangeEnd(undefined);
        delete updatedFilters.numbers_range;
        closePopover('numberOrder');
        break;
      case 'date':
        setStartDateStart(undefined);
        setStartDateEnd(undefined);
        setEndDateStart(undefined);
        setEndDateEnd(undefined);
        setCreationDateStart(undefined);
        setCreationDateEnd(undefined);
        setPaidDateStart(undefined);
        setPaidDateEnd(undefined);
        delete updatedFilters.date_start_range;
        delete updatedFilters.date_end_range;
        delete updatedFilters.create_date_range;
        delete updatedFilters.date_paid_range;
        closePopover('date');
        break;
      case 'actStatuses':
        setActData({
          number: undefined,
          statuses: [],
          create_date_range: undefined,
        });
        delete updatedFilters.act;
        closePopover('acts');
        break;
      case 'receiptStatuses':
        setReceiptData({
          number: undefined,
          statuses: [],
        });
        delete updatedFilters.receipts;
        closePopover('receipts');
        break;
    }

    onApplyFilters(updatedFilters);
  };

  const hasActiveFilters = () => {
    return (
      (initialFilters.type_ids && initialFilters.type_ids.length > 0) ||
      (initialFilters.customer_ids && initialFilters.customer_ids.length > 0) ||
      (initialFilters.worker_ids && initialFilters.worker_ids.length > 0) ||
      (initialFilters.preset_ids && initialFilters.preset_ids.length > 0) ||
      (initialFilters.statuses && initialFilters.statuses.length > 0) ||
      (initialFilters.payment_type_ids && initialFilters.payment_type_ids.length > 0) ||
      initialFilters.amount_from !== undefined ||
      initialFilters.amount_to !== undefined ||
      (initialFilters.numbers_range &&
        (initialFilters.numbers_range.start !== '' ||
          initialFilters.numbers_range.end !== '')) ||
      initialFilters.date_start_range ||
      initialFilters.date_end_range ||
      initialFilters.create_date_range ||
      initialFilters.date_paid_range ||
      (initialFilters.act?.statuses && initialFilters.act.statuses.length > 0) ||
      initialFilters.act?.number ||
      initialFilters.act?.create_date_range ||
      (initialFilters.receipts?.statuses &&
        initialFilters.receipts.statuses.length > 0) ||
      initialFilters.receipts?.number !== undefined
    );
  };

  const countActiveFilters = () => {
    let count = 0;

    if (initialFilters.type_ids?.length) count++;
    if (initialFilters.customer_ids?.length) count++;
    if (initialFilters.worker_ids?.length) count++;
    if (initialFilters.preset_ids?.length) count++;
    if (initialFilters.statuses?.length) count++;
    if (initialFilters.payment_type_ids?.length) count++;
    if (initialFilters.amount_from != null || initialFilters.amount_to != null) count++;
    if (
      initialFilters.numbers_range &&
      (initialFilters.numbers_range.start !== '' ||
        initialFilters.numbers_range.end !== '')
    )
      count++;
    if (initialFilters.date_start_range) count++;
    if (initialFilters.date_end_range) count++;
    if (initialFilters.create_date_range) count++;
    if (initialFilters.date_paid_range) count++;
    if (initialFilters.act?.statuses?.length) count++;
    if (initialFilters.act?.number != null) count++;
    if (initialFilters.act?.create_date_range) count++;
    if (initialFilters.receipts?.statuses?.length) count++;
    if (initialFilters.receipts?.number != null) count++;

    return count;
  };

  const isCustomerFilterApplied = (): boolean => {
    return !!(customerIds && customerIds.length > 0);
  };

  const isTypeFilterApplied = (): boolean => {
    return !!(typeIds && typeIds.length > 0);
  };

  const isStatusFilterApplied = (): boolean => {
    return !!(statusIds && statusIds.length > 0);
  };

  const isAmountFilterApplied = (): boolean => {
    return !!(amountFrom !== undefined || amountTo !== undefined);
  };

  const isNumbersFilterApplied = (): boolean => {
    return !!(numbersRangeStart !== undefined || numbersRangeEnd !== undefined);
  };

  const isDateFilterApplied = (): boolean => {
    return !!(
      startDateStart ||
      startDateEnd ||
      endDateStart ||
      endDateEnd ||
      creationDateStart ||
      creationDateEnd ||
      paidDateStart ||
      paidDateEnd
    );
  };

  const isWorkerFilterApplied = (): boolean => {
    return !!(workerIds && workerIds.length > 0);
  };

  const isPresetFilterApplied = (): boolean => {
    return !!(presetIds && presetIds.length > 0);
  };

  const isReceiptFilterApplied = (): boolean => {
    return !!(
      (receiptData.number !== undefined && receiptData.number !== '') ||
      (receiptData.statuses && receiptData.statuses.length > 0)
    );
  };

  const isActFilterApplied = (): boolean => {
    return !!(
      actData.number ||
      (actData.statuses && actData.statuses.length > 0) ||
      actData.create_date_range
    );
  };

  const isPaymentTypeFilterApplied = (): boolean => {
    return !!(paymentTypeIds.length > 0);
  };

  const handleDateChange = (dates: {
    startDateStart?: Date;
    startDateEnd?: Date;
    endDateStart?: Date;
    endDateEnd?: Date;
    creationDateStart?: Date;
    creationDateEnd?: Date;
    paidDateStart?: Date;
    paidDateEnd?: Date;
  }) => {
    setStartDateStart(dates.startDateStart);
    setStartDateEnd(dates.startDateEnd);
    setEndDateStart(dates.endDateStart);
    setEndDateEnd(dates.endDateEnd);
    setCreationDateStart(dates.creationDateStart);
    setCreationDateEnd(dates.creationDateEnd);
    setPaidDateStart(dates.paidDateStart);
    setPaidDateEnd(dates.paidDateEnd);
  };

  const formatDateForAPI = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDateApply = (dates: {
    startDateStart?: Date;
    startDateEnd?: Date;
    endDateStart?: Date;
    endDateEnd?: Date;
    creationDateStart?: Date;
    creationDateEnd?: Date;
  }) => {
    const filters: OrderFilterParams = {};

    if (typeIds.length > 0) filters.type_ids = typeIds;
    if (customerIds.length > 0) filters.customer_ids = customerIds;
    if (workerIds.length > 0) filters.worker_ids = workerIds;
    if (presetIds.length > 0) filters.preset_ids = presetIds;
    if (statusIds.length > 0) filters.statuses = statusIds;
    if (paymentTypeIds.length > 0) filters.payment_type_ids = paymentTypeIds;
    if (amountFrom !== undefined) filters.amount_from = amountFrom;
    if (amountTo !== undefined) filters.amount_to = amountTo;

    if (dates.startDateStart || dates.startDateEnd) {
      filters.date_start_range = {
        start: dates.startDateStart
          ? formatDateForAPI(dates.startDateStart)
          : dates.startDateEnd
          ? formatDateForAPI(dates.startDateEnd)
          : '',
        end: dates.startDateEnd
          ? formatDateForAPI(dates.startDateEnd)
          : dates.startDateStart
          ? formatDateForAPI(dates.startDateStart)
          : '',
      };
    }

    if (dates.endDateStart || dates.endDateEnd) {
      filters.date_end_range = {
        start: dates.endDateStart
          ? formatDateForAPI(dates.endDateStart)
          : dates.endDateEnd
          ? formatDateForAPI(dates.endDateEnd)
          : '',
        end: dates.endDateEnd
          ? formatDateForAPI(dates.endDateEnd)
          : dates.endDateStart
          ? formatDateForAPI(dates.endDateStart)
          : '',
      };
    }

    if (dates.creationDateStart || dates.creationDateEnd) {
      filters.create_date_range = {
        start: dates.creationDateStart
          ? formatDateForAPI(dates.creationDateStart)
          : dates.creationDateEnd
          ? formatDateForAPI(dates.creationDateEnd)
          : '',
        end: dates.creationDateEnd
          ? formatDateForAPI(dates.creationDateEnd)
          : dates.creationDateStart
          ? formatDateForAPI(dates.creationDateStart)
          : '',
      };
    }

    if (paidDateStart || paidDateEnd) {
      filters.date_paid_range = {
        start: paidDateStart
          ? formatDateForAPI(paidDateStart)
          : paidDateEnd
          ? formatDateForAPI(paidDateEnd)
          : '',
        end: paidDateEnd
          ? formatDateForAPI(paidDateEnd)
          : paidDateStart
          ? formatDateForAPI(paidDateStart)
          : '',
      };
    }

    if (
      actData.number ||
      (actData.statuses && actData.statuses.length > 0) ||
      actData.create_date_range
    ) {
      filters.act = actData;
    }

    if (
      receiptData.number !== undefined ||
      (receiptData.statuses && receiptData.statuses.length > 0)
    ) {
      filters.receipts = receiptData;
    }

    onApplyFilters(filters);
    closePopover('date');
  };

  const handleActApply = (newActData: ActFilterData) => {
    setActData(newActData);

    const filters = getCurrentFilters();
    filters.act = newActData;

    onApplyFilters(filters);
    closePopover('acts');
  };

  const handleReceiptApply = (newReceiptData: ReceiptFilterData) => {
    setReceiptData(newReceiptData);

    const filters = getCurrentFilters();
    filters.receipts = newReceiptData;

    onApplyFilters(filters);
    closePopover('receipts');
  };

  const [queryParamsTypes, setQueryParamsTypes] = useState({
    page: 1,
    per_page: 20,
    search: '',
  });

  const handleTypesServerSearch = useCallback((search: string) => {
    setQueryParamsTypes((prev) => {
      if (prev.page === 1 && prev.search === search) return prev;
      return {
        ...prev,
        page: 1,
        search,
      };
    });
  }, []);

  const {
    data: typesFiltersData,
    isLoading: isLoadingTypes,
    isFetching: isFetchingTypes,
  } = useGetTypesFiltersQuery(
    {
      page: queryParamsTypes.page,
      per_page: queryParamsTypes.per_page,
      ...(queryParamsTypes.search ? { search: queryParamsTypes.search } : {}),
    },
    { skip: !isFiltersOpen },
  );

  const hasNextTypesPage =
    !!typesFiltersData?.meta &&
    typesFiltersData.meta.current_page < typesFiltersData.meta.last_page;

  const loadMoreTypes = useCallback(() => {
    if (isFetchingTypes) return;
    if (!hasNextTypesPage) return;
    setQueryParamsTypes((prev) => ({ ...prev, page: prev.page + 1 }));
  }, [isFetchingTypes, hasNextTypesPage]);

  const [queryParamsBeneficiaries, setQueryParamsBeneficiaries] = useState({
    page: 1,
    per_page: 20,
    search: '',
  });

  const handleBeneficiariesServerSearch = useCallback((search: string) => {
    setQueryParamsBeneficiaries((prev) => {
      if (prev.page === 1 && prev.search === search) return prev;
      return {
        ...prev,
        page: 1,
        search,
      };
    });
  }, []);

  const {
    data: beneficiariesFiltersData,
    isLoading: isLoadingBeneficiaries,
    isFetching: isFetchingBeneficiaries,
  } = useGetTabPaginationBeneficiarQuery(
    {
      page: queryParamsBeneficiaries.page,
      per_page: queryParamsBeneficiaries.per_page,
      ...(queryParamsBeneficiaries.search
        ? { search: queryParamsBeneficiaries.search }
        : {}),
    },
    { skip: !isFiltersOpen || role !== 'worksupport' },
  );

  const hasNextBeneficiariesPage =
    !!beneficiariesFiltersData?.meta &&
    beneficiariesFiltersData.meta.current_page < beneficiariesFiltersData.meta.last_page;

  const loadMoreBeneficiaries = useCallback(() => {
    if (isFetchingBeneficiaries) return;
    if (!hasNextBeneficiariesPage) return;
    setQueryParamsBeneficiaries((prev) => ({ ...prev, page: prev.page + 1 }));
  }, [isFetchingBeneficiaries, hasNextBeneficiariesPage]);

  const [queryParamsTemplates, setQueryParamsTemplates] = useState({
    page: 1,
    per_page: 20,
    search: '',
  });

  const handleTemplatesServerSearch = useCallback((search: string) => {
    setQueryParamsTemplates((prev) => {
      if (prev.page === 1 && prev.search === search) return prev;
      return {
        ...prev,
        page: 1,
        search,
      };
    });
  }, []);

  const {
    data: templatesFiltersData,
    isLoading: isLoadingTemplates,
    isFetching: isFetchingTemplates,
  } = useGetOrderTemplatesFiltersQuery(
    {
      page: queryParamsTemplates.page,
      per_page: queryParamsTemplates.per_page,
      ...(queryParamsTemplates.search ? { search: queryParamsTemplates.search } : {}),
    },
    { skip: !isFiltersOpen },
  );

  const hasNextTemplatesPage =
    !!templatesFiltersData?.meta &&
    templatesFiltersData.meta.current_page < templatesFiltersData.meta.last_page;

  const loadMoreTemplates = useCallback(() => {
    if (isFetchingTemplates) return;
    if (!hasNextTemplatesPage) return;
    setQueryParamsTemplates((prev) => ({ ...prev, page: prev.page + 1 }));
  }, [isFetchingTemplates, hasNextTemplatesPage]);

  const [queryParamsWorkers, setQueryParamsWorkers] = useState({
    page: 1,
    per_page: 20,
    searchValue: '',
  });

  const handleWorkersServerSearch = useCallback((searchValue: string) => {
    setQueryParamsWorkers((prev) => {
      if (prev.page === 1 && prev.searchValue === searchValue) return prev;
      return {
        ...prev,
        page: 1,
        searchValue,
      };
    });
  }, []);

  const {
    data: workersFiltersData,
    isLoading: isLoadingWorkers,
    isFetching: isFetchingWorkers,
  } = useGetEmployedsQuery(
    {
      page: queryParamsWorkers.page,
      per_page: queryParamsWorkers.per_page,
      searchValue: queryParamsWorkers.searchValue,
    },
    { skip: !isFiltersOpen || role !== 'director' },
  );
  const {
    data: workersFiltersDataWorkSupport,
    isLoading: isLoadingWorkersWorkSupport,
    isFetching: isFetchingWorkersWorkSupport,
  } = useGetTabPaginationEmployedsQuery(
    {
      page: queryParamsWorkers.page,
      per_page: queryParamsWorkers.per_page,
      search: queryParamsWorkers.searchValue || undefined,
    },
    { skip: !isFiltersOpen || role !== 'worksupport' },
  );

  const hasNextWorkersPage =
    role === 'director'
      ? !!workersFiltersData?.meta &&
        workersFiltersData.meta.current_page < workersFiltersData.meta.last_page
      : !!workersFiltersDataWorkSupport?.meta &&
        workersFiltersDataWorkSupport.meta.current_page <
          workersFiltersDataWorkSupport.meta.last_page;

  const loadMoreWorkers = useCallback(() => {
    if (isFetchingWorkers) return;
    if (!hasNextWorkersPage) return;
    setQueryParamsWorkers((prev) => ({ ...prev, page: prev.page + 1 }));
  }, [isFetchingWorkers, hasNextWorkersPage]);

  const transformCustomers = (): AdaptiveFilterOption[] => {
    return (beneficiariesFiltersData?.data || []).map((customer: ItemTypes) => ({
      id: customer.id?.toString() || '',
      label: customer.jur_name || '',
      inn: customer.inn || undefined,
    }));
  };

  const transformTypes = (): AdaptiveFilterOption[] => {
    return (typesFiltersData?.items || []).map((type) => ({
      id: type.id?.toString() || '',
      label: type.name,
    }));
  };

  const transformPaymentTypes = [
    {
      id: 'fixed',
      label: 'Фиксированная',
    },
    {
      id: 'unit_based',
      label: 'Оплата за выполненный объем',
    },
  ];

  const transformWorkers = (): AdaptiveFilterOption[] => {
    return (
      role === 'director'
        ? workersFiltersData?.data || []
        : workersFiltersDataWorkSupport?.data || []
    ).map((worker) => ({
      id:
        role === 'director' ? worker.id?.toString() : worker.worker_id?.toString() || '',
      label: `${worker.surname} ${worker.name} ${worker.patronymic || ''}`,
    }));
  };

  const transformPresets = (): AdaptiveFilterOption[] => {
    return (templatesFiltersData?.data || []).map((preset) => ({
      id: preset.id?.toString() || '',
      label: preset.name || '',
    }));
  };

  const transformStatuses = (): AdaptiveFilterOption[] => {
    return (filtersData?.statuses || []).map((status) => ({
      id: status.name || '',
      label: status.label,
    }));
  };

  const transformReceiptStatusesForFilter = (): Array<{
    value: string;
    label: string;
  }> => {
    return (filtersData?.receipt_statuses || []).map((status) => ({
      value: status.name || '',
      label: status.label,
    }));
  };

  return (
    <div className={styles.filtersTablet} ref={filtersRef}>
      <button
        className={`${styles.filterButton} ${hasActiveFilters() ? styles.active : ''}`}
        onClick={() => setIsFiltersOpen((prev) => !prev)}>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          {!hasActiveFilters() && (
            <img src={iconFilter} alt='Фильтр' className={styles.filterIcon} />
          )}
          <span
            style={{
              color: hasActiveFilters() ? '#002CFB' : undefined,
            }}>
            Фильтр
          </span>
          {hasActiveFilters() && countActiveFilters() > 1 ? (
            <div className={styles.test}>{countActiveFilters()} </div>
          ) : null}
          {hasActiveFilters() && (
            <IconCloseBlue
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleReset();
              }}
            />
          )}
        </div>
      </button>
      {isFiltersOpen && (
        <div className={styles.filtersDropdown}>
          <span className={styles.filtersTitle}>Фильтр</span>
          <div className={styles.sections}>
            {role === 'worksupport' && (
              <div
                className={`${styles.section} ${
                  openSection === 'customers' ? styles.sectionActive : ''
                }`}>
                <button
                  type='button'
                  className={styles.sectionHeader}
                  onClick={() => toggleSection('customers')}>
                  <div className={styles.sectionHeaderContent}>
                    <IconWork className={styles.sectionIcon} />
                    <div className={styles.textActions}>
                      <span>Заказчик</span>
                      {isCustomerFilterApplied() && <span className={styles.activeDot} />}
                    </div>
                  </div>
                  <span className={styles.sectionArrow}>
                    {openSection === 'customers' ? <IconChevronUp /> : <IconChevron />}
                  </span>
                </button>

                {openSection === 'customers' && (
                  <div className={styles.sectionBody}>
                    <TabletAdaptiveFilter
                      options={transformCustomers()}
                      selectedValues={customerIds}
                      onChange={(values) => setCustomerIds(values as SelectedItem[])}
                      onReset={() => handleResetIndividualFilter('customers')}
                      onApply={handleApplyFilters}
                      serverSearch={true}
                      onServerSearchChange={handleBeneficiariesServerSearch}
                      hasNextPage={hasNextBeneficiariesPage}
                      isFetchingMore={isFetchingBeneficiaries}
                      onLoadMore={loadMoreBeneficiaries}
                      isLoading={isLoadingBeneficiaries}
                    />
                  </div>
                )}
              </div>
            )}

            <div
              className={`${styles.section} ${
                openSection === 'numberOrder' ? styles.sectionActive : ''
              }`}>
              <button
                type='button'
                className={styles.sectionHeader}
                onClick={() => toggleSection('numberOrder')}>
                <div className={styles.sectionHeaderContent}>
                  <IconSearchNumber className={styles.sectionIcon} />
                  <div className={styles.textActions}>
                    <span>Номера заказов</span>
                    {isNumbersFilterApplied() && <span className={styles.activeDot} />}
                  </div>
                </div>
                <span className={styles.sectionArrow}>
                  {openSection === 'numberOrder' ? <IconChevronUp /> : <IconChevron />}
                </span>
              </button>

              {openSection === 'numberOrder' && (
                <div className={styles.sectionBody}>
                  <TabletPriceFilter
                    show={false}
                    minValue={numbersRangeStart}
                    maxValue={numbersRangeEnd}
                    onChange={(min, max) => {
                      setNumbersRangeStart(min);
                      setNumbersRangeEnd(max);
                    }}
                    onReset={() => handleResetIndividualFilter('numberOrder')}
                    onApply={handleApplyFilters}
                    fromPlaceholder={'№ заказа'}
                    toPlaceholder={'№ заказа'}
                  />
                </div>
              )}
            </div>

            <div
              className={`${styles.section} ${
                openSection === 'serviceType' ? styles.sectionActive : ''
              }`}>
              <button
                type='button'
                className={styles.sectionHeader}
                onClick={() => toggleSection('serviceType')}>
                <div className={styles.sectionHeaderContent}>
                  <IconWork className={styles.sectionIcon} />
                  <div className={styles.textActions}>
                    <span>Тип услуги</span>
                    {isTypeFilterApplied() && <span className={styles.activeDot} />}
                  </div>
                </div>
                <span className={styles.sectionArrow}>
                  {openSection === 'serviceType' ? <IconChevronUp /> : <IconChevron />}
                </span>
              </button>

              {openSection === 'serviceType' && (
                <div className={styles.sectionBody}>
                  <TabletAdaptiveFilter
                    options={transformTypes()}
                    selectedValues={typeIds}
                    onChange={(values) => setTypeIds(values as SelectedItem[])}
                    onReset={() => handleResetIndividualFilter('types')}
                    onApply={handleApplyFilters}
                    serverSearch={true}
                    onServerSearchChange={handleTypesServerSearch}
                    hasNextPage={hasNextTypesPage}
                    isFetchingMore={isFetchingTypes}
                    onLoadMore={loadMoreTypes}
                    isLoading={isLoadingTypes}
                  />
                </div>
              )}
            </div>

            <div
              className={`${styles.section} ${
                openSection === 'paymentType' ? styles.sectionActive : ''
              }`}>
              <button
                type='button'
                className={styles.sectionHeader}
                onClick={() => toggleSection('paymentType')}>
                <div className={styles.sectionHeaderContent}>
                  <IconPayment className={styles.sectionIcon} />
                  <div className={styles.textActions}>
                    <span>Тип оплаты</span>
                    {isPaymentTypeFilterApplied() && (
                      <span className={styles.activeDot} />
                    )}
                  </div>
                </div>
                <span className={styles.sectionArrow}>
                  {openSection === 'paymentType' ? <IconChevronUp /> : <IconChevron />}
                </span>
              </button>
              {openSection === 'paymentType' && (
                <div className={styles.sectionBody}>
                  <TabletAdaptiveFilter
                    options={transformPaymentTypes}
                    selectedValues={paymentTypeIds}
                    onChange={(values) => setPaymentTypeIds(values as SelectedItem[])}
                    onReset={() => handleResetIndividualFilter('paymentType')}
                    onApply={handleApplyFilters}
                    showSearch={false}
                    singleSelect
                  />
                </div>
              )}
            </div>

            <div
              className={`${styles.section} ${
                openSection === 'status' ? styles.sectionActive : ''
              }`}>
              <button
                type='button'
                className={styles.sectionHeader}
                onClick={() => toggleSection('status')}>
                <div className={styles.sectionHeaderContent}>
                  <IconOrdersStatus className={styles.sectionIcon} />
                  <div className={styles.textActions}>
                    <span>Статус заказа</span>
                    {isStatusFilterApplied() && <span className={styles.activeDot} />}
                  </div>
                </div>
                <span className={styles.sectionArrow}>
                  {openSection === 'status' ? <IconChevronUp /> : <IconChevron />}
                </span>
              </button>

              {openSection === 'status' && (
                <div className={styles.sectionBody}>
                  <TabletAdaptiveFilter
                    options={transformStatuses()}
                    selectedValues={statusIds}
                    onChange={setStatusIds}
                    onReset={() => handleResetIndividualFilter('statuses')}
                    onApply={handleApplyFilters}
                  />
                </div>
              )}
            </div>

            <div
              className={`${styles.section} ${
                openSection === 'amount' ? styles.sectionActive : ''
              }`}>
              <button
                type='button'
                className={styles.sectionHeader}
                onClick={() => toggleSection('amount')}>
                <div className={styles.sectionHeaderContent}>
                  <IconCalculator className={styles.sectionIcon} />
                  <div className={styles.textActions}>
                    <span>Сумма</span>
                    {isAmountFilterApplied() && <span className={styles.activeDot} />}
                  </div>
                </div>
                <span className={styles.sectionArrow}>
                  {openSection === 'amount' ? <IconChevronUp /> : <IconChevron />}
                </span>
              </button>

              {openSection === 'amount' && (
                <div className={styles.sectionBody}>
                  <TabletPriceFilter
                    show={true}
                    minValue={amountFrom}
                    maxValue={amountTo}
                    fromPlaceholder={'0'}
                    toPlaceholder={'1 000 000'}
                    onChange={(min, max) => {
                      setAmountFrom(min);
                      setAmountTo(max);
                    }}
                    onReset={() => handleResetIndividualFilter('amount')}
                    onApply={handleApplyFilters}
                  />
                </div>
              )}
            </div>

            <div
              className={`${styles.section} ${
                openSection === 'date' ? styles.sectionActive : ''
              }`}>
              <button
                type='button'
                className={styles.sectionHeader}
                onClick={() => toggleSection('date')}>
                <div className={styles.sectionHeaderContent}>
                  <IconCalendar className={styles.sectionIcon} />
                  <div className={styles.textActions}>
                    <span>Дата</span>
                    {isDateFilterApplied() && <span className={styles.activeDot} />}
                  </div>
                </div>
                <span className={styles.sectionArrow}>
                  {openSection === 'date' ? <IconChevronUp /> : <IconChevron />}
                </span>
              </button>

              {openSection === 'date' && (
                <div className={styles.sectionBody}>
                  <TabletDateFilter
                    startDateStart={startDateStart}
                    startDateEnd={startDateEnd}
                    endDateStart={endDateStart}
                    endDateEnd={endDateEnd}
                    creationDateStart={creationDateStart}
                    creationDateEnd={creationDateEnd}
                    paidDateStart={paidDateStart}
                    paidDateEnd={paidDateEnd}
                    onChange={handleDateChange}
                    onReset={() => handleResetIndividualFilter('date')}
                    onApply={handleDateApply}
                  />
                </div>
              )}
            </div>

            <div
              className={`${styles.section} ${
                openSection === 'executor' ? styles.sectionActive : ''
              }`}>
              <button
                type='button'
                className={styles.sectionHeader}
                onClick={() => toggleSection('executor')}>
                <div className={styles.sectionHeaderContent}>
                  <IconPerson className={styles.sectionIcon} />
                  <div className={styles.textActions}>
                    <span>Исполнитель</span>
                    {isWorkerFilterApplied() && <span className={styles.activeDot} />}
                  </div>
                </div>
                <span className={styles.sectionArrow}>
                  {openSection === 'executor' ? <IconChevronUp /> : <IconChevron />}
                </span>
              </button>

              {openSection === 'executor' && (
                <div className={styles.sectionBody}>
                  <TabletAdaptiveFilter
                    options={transformWorkers()}
                    selectedValues={workerIds}
                    onChange={(values) => setWorkerIds(values as SelectedItem[])}
                    onReset={() => handleResetIndividualFilter('workers')}
                    onApply={handleApplyFilters}
                    serverSearch={true}
                    onServerSearchChange={handleWorkersServerSearch}
                    hasNextPage={hasNextWorkersPage}
                    isFetchingMore={isFetchingWorkers || isFetchingWorkersWorkSupport}
                    onLoadMore={loadMoreWorkers}
                    isLoading={isLoadingWorkers || isLoadingWorkersWorkSupport}
                  />
                </div>
              )}
            </div>

            <div
              className={`${styles.section} ${
                openSection === 'template' ? styles.sectionActive : ''
              }`}>
              <button
                type='button'
                className={styles.sectionHeader}
                onClick={() => toggleSection('template')}>
                <div className={styles.sectionHeaderContent}>
                  <IconBookOpen className={styles.sectionIcon} />
                  <div className={styles.textActions}>
                    <span>Шаблон</span>
                    {isPresetFilterApplied() && <span className={styles.activeDot} />}
                  </div>
                </div>
                <span className={styles.sectionArrow}>
                  {openSection === 'template' ? <IconChevronUp /> : <IconChevron />}
                </span>
              </button>

              {openSection === 'template' && (
                <div className={styles.sectionBody}>
                  <TabletAdaptiveFilter
                    options={transformPresets()}
                    selectedValues={presetIds}
                    onChange={(values) => setPresetIds(values as SelectedItem[])}
                    onReset={() => handleResetIndividualFilter('presets')}
                    onApply={handleApplyFilters}
                    serverSearch={true}
                    onServerSearchChange={handleTemplatesServerSearch}
                    hasNextPage={hasNextTemplatesPage}
                    isFetchingMore={isFetchingTemplates}
                    onLoadMore={loadMoreTemplates}
                    isLoading={isLoadingTemplates}
                  />
                </div>
              )}
            </div>

            <div
              className={`${styles.section} ${
                openSection === 'check' ? styles.sectionActive : ''
              }`}>
              <button
                type='button'
                className={styles.sectionHeader}
                onClick={() => toggleSection('check')}>
                <div className={styles.sectionHeaderContent}>
                  <IconBill className={styles.sectionIcon} />
                  <div className={styles.textActions}>
                    <span>Чек</span>
                    {isReceiptFilterApplied() && <span className={styles.activeDot} />}
                  </div>
                </div>
                <span className={styles.sectionArrow}>
                  {openSection === 'check' ? <IconChevronUp /> : <IconChevron />}
                </span>
              </button>

              {openSection === 'check' && (
                <div className={styles.sectionBody}>
                  <TabletReceiptFilter
                    initialData={receiptData}
                    onApply={handleReceiptApply}
                    onReset={() => handleResetIndividualFilter('receiptStatuses')}
                    onChange={(data) => setReceiptData(data)}
                    statusOptions={transformReceiptStatusesForFilter()}
                  />
                </div>
              )}
            </div>

            <div
              className={`${styles.section} ${
                openSection === 'act' ? styles.sectionActive : ''
              }`}>
              <button
                type='button'
                className={styles.sectionHeader}
                onClick={() => toggleSection('act')}>
                <div className={styles.sectionHeaderContent}>
                  <IconClipboardCheck className={styles.sectionIcon} />
                  <div className={styles.textActions}>
                    <span>Акт</span>
                    {isActFilterApplied() && <span className={styles.activeDot} />}
                  </div>
                </div>
                <span className={styles.sectionArrow}>
                  {openSection === 'act' ? <IconChevronUp /> : <IconChevron />}
                </span>
              </button>
              {openSection === 'act' && (
                <div className={styles.sectionBody}>
                  <TabletActFilter
                    initialData={actData}
                    onApply={handleActApply}
                    onReset={() => handleResetIndividualFilter('actStatuses')}
                    onChange={(data) => setActData(data)}
                  />
                </div>
              )}
            </div>
          </div>

          <div className={styles.footer}>
            <Button className={styles.resetButton} theme='outline' onClick={handleReset}>
              <span
                style={{
                  marginRight: '6px',
                }}>
                Сбросить
              </span>
            </Button>
            <Button className={styles.btnDone} onClick={handleApplyFilters} theme='filed'>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginLeft: '6px',
                }}>
                <span>Применить</span> <IconDone style={{ width: 20, height: 20 }} />
              </div>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
